import React, { useState, useEffect, Fragment } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import './assets/style.css';
import { Breadcrumbs } from '../../../components/Atom/breadcumbs';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import ShortenId from 'components/Atom/ShortenId';
import StatusTag from 'components/Atom/StatusTag';
import { Button as ANTButton, Empty, notification } from 'antd';
import { useSelector } from 'react-redux';
import { getBillingInvoiceDetail, billingEmailInvoicePdf } from 'api/subscriptions';
import { useQuery } from 'react-query';
import { capitalizeWords } from 'utils/helper';
import { checkUserPermissions, formatAmount } from 'utils/services';
import { USER_ROLES, TIMEZONES } from 'utils/constant';
import PricingModelBreakdown from './assets/PricingModelBreakdown';
import LoadingSkeleton from '../../../components/Skeleton/index';
import { ResetButton, Button } from '../../../components/Atom/Button';
import { formatInTimeZone } from 'date-fns-tz';

export const ViewBillingInvoiceDetails = () => {
  const location = useLocation();
  const { id } = useParams();
  const store = useSelector((state) => state.company.store);
  const navigate = useNavigate();
  const [isInvoicePdfSending, setIsInvoicePdfSending] = useState(false);
  // const [record, setRecord] = useState(location?.state?.record);
  const fetchInvoiceData = async () => {
    const response = await getBillingInvoiceDetail(store?._id, id);
    return response?.data?.data || null;
  };
  const {
    data: record,
    refetch,
    isLoading,
    isFetching,
  } = useQuery(`invoices_${id || location?.state?.record?.invoice_id}`, fetchInvoiceData, {
    enabled: false,
    manual: true,
  });

  useEffect(() => {
    refetch();
  }, [store]);

  const handleEmailInvoicePdf = async () => {
    setIsInvoicePdfSending(true);
    try {
      const payload = {
        invoice_id: id || location?.state?.id,
        mode: 'invoice_customer',
      };
      await billingEmailInvoicePdf(store?._id, payload);
    } catch (error) {
      notification?.error({
        message: 'Error',
        description: 'Failed to send invoice PDF to the customer',
        placement: 'bottomRight',
      });
    } finally {
      setIsInvoicePdfSending(false);
    }
  };

  const breadcrumbProps = {
    breadcrumbs: [
      {
        name: 'XPay',
        href: '/xpay',
      },
      {
        name: 'Account Settings',
        href: '/setting/billing',
        data: { id: location?.state?.id, isRedirection: true, filtersData: location?.state?.filtersData, searchValue: location?.state?.searchValue },
      },
      {
        name: 'Billing & Usage',
        href: '/setting/billing',
        data: { id: location?.state?.id, isRedirection: true, filtersData: location?.state?.filtersData, searchValue: location?.state?.searchValue },
      },
      {
        name: <ShortenId value={id || location?.state?.id || 'Not Found'} />,
      },
    ],
  };
  const time_zone = store?.settings?.timezone || TIMEZONES.PKT;
  const generalInformation = [
    { label: 'Invoice ID', value: <ShortenId value={record?.invoice_id} /> },
    { label: 'Plan ID', value: <ShortenId value={record?.plan_id} /> },
    { label: 'Subscription ID', value: <ShortenId value={record?.subscription_id?.subscription_id || record?.subscription_id} /> },
    { label: 'Status', value: <StatusTag tag={record?.state === 'draft' ? 'pending' : record?.state} /> },
    { label: 'Last Update Time', value: record?.updatedAt ? formatInTimeZone(record?.updatedAt, time_zone, 'MMM d, yyyy h:mm:ss a') : 'N/A' },
    ...(record?.combination_operator ? [{ label: 'Combination Operator', value: record?.combination_operator || 'N/A' }] : []),
    ...(record?.or_strategy ? [{ label: 'Whichever Strategy', value: record?.or_strategy || 'N/A' }] : []),
    ...(record?.is_csv_based ? [{ label: 'CSV Based', value: record?.is_csv_based ? 'Yes' : 'No' }] : []),
    ...(record?.csv_amount ? [{ label: 'CSV Amount', value: record?.csv_amount || 'N/A' }] : []),
    ...(record?.components?.[0]?.base_amount ? [{ label: 'Base Amount', value: record?.components?.[0]?.base_amount?.toLocaleString('en-US', { maximumSignificantDigits: 21 }) || 'N/A' }] : []),
  ];

  const paymentDetails = [
    { label: 'Payment Token', value: <ShortenId value={record?.subscription?.payment?.token || record?.payment?.token || 'N/A'} /> },
    { label: 'Total Amount', value: formatAmount(record?.amount || 0, record?.currency) || '--' },
    { label: 'Currency', value: record?.currency || 'N/A' },
    { label: 'Payment Date', value: record?.payment_date ? formatInTimeZone(record?.payment_date, time_zone, 'MMM d, yyyy h:mm:ss a') : 'N/A' },
  ];

  return (
    <>
      <div className='header-container p-4 custom-sticky'>
        <div className='payment-breadcrums'>
          <div
            className='back-button'
            onClick={() =>
              navigate('/setting/billing', {
                state: {
                  id: location?.state?.id,
                  isRedirection: true,
                  filtersData: location?.state?.filtersData,
                  searchValue: location?.state?.searchValue,
                },
              })
            }>
            <ArrowLeftOutlined /> <span style={{ fontWeight: 600 }}>Back</span>
          </div>
          <span style={{ color: '#8C8C8C' }}>|</span>
          <Breadcrumbs {...breadcrumbProps} />
          <ResetButton onClick={refetch} loading={isFetching} />
        </div>
        <div className='heading-row' style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div className='detail-heading mb-4 mt-4'>Invoice Usage Detail</div>
          {checkUserPermissions([USER_ROLES.XPAY_ADMIN, USER_ROLES.XPAY_RESTRICTED_ADMIN, USER_ROLES.XPAY_SUBSCRIPTION_USER]) &&
            record?.pricing_model_breakdown &&
            record?.pricing_model_breakdown?.length > 0 && (
              <Button loading={isInvoicePdfSending} disabled={isInvoicePdfSending} className='custom-btns p-2 flex items-center' onClick={handleEmailInvoicePdf}>
                <span className='text-base'>Email PDF</span>
              </Button>
            )}
        </div>
      </div>
      <div className='subscription-details logs-details'>
        {isLoading ? (
          <LoadingSkeleton />
        ) : !record ? (
          store?.isSelected ? (
            <div className='empty-custom-style'>
              <Empty image={Empty?.PRESENTED_IMAGE_SIMPLE} />
            </div>
          ) : (
            <div className='empty-custom-style'>
              <Empty description={'Select store from the drop-down menu above'} />
            </div>
          )
        ) : (
          <>
            <div className='details-section '>
              <div className='heading-section'>
                <h3>Invoice Detail</h3>
              </div>
              <div className='detail-card'>
                {generalInformation?.map((info, index) => (
                  <div key={index} className='detail-value'>
                    <h2>{info?.label}</h2> <span>{info?.value || 'N/A'}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className='details-section'>
              <div className='heading-section'>
                <h3>Payment Details</h3>
              </div>
              <div className='detail-card'>
                {paymentDetails?.map((info, index) => (
                  <div key={index} className='detail-value'>
                    <h2>{info?.label}</h2> <span>{info?.value || 'N/A'}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className='details-section'>
              <div className='heading-section'>
                <h3>Customer Details</h3>
              </div>
              <div className='detail-card'>
                <div className='detail-value'>
                  <h2>Name</h2> <span>{record?.subscription?.customer?.name || record?.customer?.name || 'N/A'}</span>
                </div>
                <div className='detail-value'>
                  <h2>Email</h2> <span>{record?.subscription?.customer?.email || record?.customer?.email || 'N/A'}</span>
                </div>
                <div className='detail-value'>
                  <h2>Phone</h2> <span>{record?.subscription?.customer?.phone || record?.customer?.phone || 'N/A'}</span>
                </div>
              </div>
            </div>

            {record?.is_csv_based && record?.csvBasedAdditionalCharges && record?.csvBasedAdditionalCharges?.length > 0 && (
              <div className='details-section'>
                <div className='heading-section'>
                  <h3>Additional Charges</h3>
                </div>
                <div className='detail-card'>
                  {record?.csvBasedAdditionalCharges?.map((charge) => (
                    <Fragment key={charge?.key}>
                      <div className='detail-value'>
                        <span>{capitalizeWords(charge?.key)}</span>
                      </div>
                      <div className='detail-value'>
                        <span>
                          {charge?.value || 'N/A'}
                          {charge?.type === 'percentage' ? '%' : ''}
                        </span>
                      </div>
                      <div className='detail-value'>
                        <span>{charge?.inclusive ? 'Inclusive' : 'Exclusive'}</span>
                      </div>
                    </Fragment>
                  ))}
                </div>
              </div>
            )}

            {/* New Pricing Model Breakdown Structure */}
            {record?.pricing_model_breakdown && record?.pricing_model_breakdown?.length > 0 && (
              <div className='details-section'>
                <div className='heading-section'>
                  <h3>Pricing Breakdown</h3>
                </div>
                {record?.pricing_model_breakdown?.map((pricingModel, idx) => (
                  <PricingModelBreakdown key={pricingModel?.pricing_model_id || idx} pricingModel={pricingModel} index={idx} currency={record?.currency} />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};
